import { useEffect, useMemo } from 'preact/hooks';
import { useSearchParams } from "react-router-dom";
import {
  atom,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
} from 'recoil';
import {Loadable} from "recoil";

export const multiParamToArray = (param) => {
    const values = [];
    for (let key in param) {
        if (param[key]) {
            values.push(key);
        }
    }
    return values;
};

export const multiParamToString = (param) => {
    return multiParamToArray(param).join("|");
};

export const stringToMultiParam = (string) => {
    return arrayToMultiParam(string.split("|").filter(v => v.length));
};

export const arrayToMultiParam = (values) => {
    let param = {};
    for (let value of values) {
        param[value] = true;
    }
    return param;
};

const createSelectParameter = (name, options, multiValue) => {
    const valueState = atom({
        key: `SELECT_PARAMETER_VALUE_${name}`,
        default: undefined
    });

    const optionsState = atom({
        key: `SELECT_PARAMETER_OPTIONS_${name}`,
        default: options
    });

    const useParameter = () => {
        let [loadable: Loadable, valueSetter] = useRecoilStateLoadable(valueState);
        const value = loadable.contents;
        const options = useRecoilValueLoadable(optionsState);

        // Use history
        const [searchParams, setSearchParams] = useSearchParams();

        // Return the default value if no value was set
        useEffect(() => {
            if (value === undefined) {
                if (searchParams.get(name) !== null) {
                    const paramValue = searchParams.get(name);
                    valueSetter(multiValue ? stringToMultiParam(paramValue) : paramValue);
                } else if (options.state === "hasValue") {
                    if (options.contents?.defaultValue) {
                        valueSetter(options.contents.defaultValue);
                    } else if (multiValue) {
                        valueSetter({});
                    }
                }
            }
        }, [value, options, valueSetter, searchParams]);


        // Whether the param values are still loading
        const loading = options.state !== "hasValue" || value === undefined;

        // Set the history
        const setter = useMemo(() => ((value) => {
            valueSetter(value);
            const paramValue = multiValue ? multiParamToString(value) : value;
            searchParams.set(name, paramValue);
            setSearchParams(searchParams);
        }), [valueSetter, setSearchParams, searchParams]);

        let returnValue = value;
        if (multiValue && !value) {
            returnValue = {};
        }
        return [returnValue, setter, options.contents?.options, loading];
    };

    return [valueState, optionsState, useParameter];
};

export const createSingleSelectParameter = (name, options) => createSelectParameter(name, options, false);
export const createMultiSelectParameter = (name, options) => createSelectParameter(name, options, true);

const createValueParameter = (name, defaultValue, deserializer, serializer) => {
    const valueState = atom({
        key: `TEXT_PARAMETER_VALUE_${name}`,
        default: defaultValue
    });

    const useParameter = () => {
        let [loadable, valueSetter] = useRecoilStateLoadable(valueState);

        // Use history
        const [searchParams, setSearchParams] = useSearchParams();

        // Set the value from URL if component just loaded
        useEffect(() => {
            if (searchParams.get(name) !== null) {
                const paramValue = searchParams.get(name);
                valueSetter(deserializer(paramValue));
            }
        });

        // Set the history
        const setter = (value) => {
            const searchParamsObject = Object.fromEntries(searchParams);
            setSearchParams({...searchParamsObject, [name]: serializer(value)});
            valueSetter(value);
        };

        return [loadable?.contents, setter];
    };

    return [valueState, useParameter];
}

export const createTextParameter = (value, defaultValue) => createValueParameter(value, defaultValue, v => v, v => v);
export const createBooleanParameter = (value, defaultValue) =>
    createValueParameter(value, defaultValue, v => (v && v !== "false"), v => v.toString());
